import { Typography } from "@circleco/compass/components/Typography";
import { useRouterState } from "@tanstack/react-router";
import { useLayoutEffect, useRef, useState } from "react";

import { isBuildItemChildActive } from "./buildItemActive";
import { CircleAIBuildNavGroup } from "./CircleAIBuildNavGroup";
import { CircleAIBuildNavRow } from "./CircleAIBuildNavRow";
import type { BuildNavItem } from "./types";

interface CircleAIBuildNavSectionProps {
  items: BuildNavItem[];
  title?: string;
}

export const CircleAIBuildNavSection = ({
  items,
  title,
}: CircleAIBuildNavSectionProps) => {
  const pathname = useRouterState({ select: s => s.location.pathname });
  const activeGroupId = items.find(item =>
    isBuildItemChildActive(item, pathname),
  )?.id;

  const [openGroupId, setOpenGroupId] = useState<string | undefined>(
    activeGroupId,
  );
  const previousActiveGroupId = useRef(activeGroupId);

  // Auto-open when route moves into a (possibly different) group.
  useLayoutEffect(() => {
    if (activeGroupId && previousActiveGroupId.current !== activeGroupId) {
      setOpenGroupId(activeGroupId);
    }
    previousActiveGroupId.current = activeGroupId;
  }, [activeGroupId]);

  if (items.length === 0) return null;

  return (
    <div className="flex flex-col gap-0.5 px-3 py-2">
      {title && (
        <Typography
          variant="heading-xs"
          color="tertiary"
          component="h2"
          className="px-3 pb-1 pt-2"
        >
          {title}
        </Typography>
      )}
      {items.map(item =>
        item.children?.length ? (
          <CircleAIBuildNavGroup
            key={item.id}
            item={item}
            isOpen={openGroupId === item.id}
            onToggle={() =>
              setOpenGroupId(current =>
                current === item.id ? undefined : item.id,
              )
            }
          />
        ) : (
          <CircleAIBuildNavRow key={item.id} item={item} />
        ),
      )}
    </div>
  );
};
